/**
 * Dashboard Currency Dropdown
 * Controlled dropdown for currency selection (no React Hook Form)
 */

import { FormControl, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback } from 'react';

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '180px',
  [theme.breakpoints.down(1520)]: {
    width: '140px',
  },
  '& .MuiInputBase-root': {
    borderRadius: '8px',
  },
  '& .MuiInputBase-input': {
    fontSize: '16px',
    [theme.breakpoints.down(1520)]: {
      fontSize: '14px',
    },
  },
  '& .MuiPaper-root': {
    boxShadow: '0px 12px 12px 0px rgba(34, 65, 102, 0.09)',
    border: '1px solid #DFDFDF',
  },
}));

interface CurrencyOption {
  label: string;
  value: string;
}

interface DashboardCurrencyDropdownProps {
  value: string;
  options: CurrencyOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
}

export const DashboardCurrencyDropdown = ({ value, options, onChange, disabled, id }: DashboardCurrencyDropdownProps) => {
  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      onChange(event.target.value);
    },
    [onChange]
  );

  const renderValue = useCallback(
    (selected: string) => {
      const option = options.find((opt) => opt.value === selected);
      return <span className='text-14 xl:text-16'>{option?.label || selected}</span>;
    },
    [options]
  );

  return (
    <StyledFormControl size='small' id={id}>
      <InputLabel>Currency Reference</InputLabel>
      <Select
        value={value || ''}
        onChange={handleChange}
        input={<OutlinedInput label='Currency Reference' />}
        renderValue={renderValue}
        disabled={disabled}
        MenuProps={{
          sx: {
            '& .MuiPaper-root': {
              boxShadow: '0px 12px 12px 0px rgba(34, 65, 102, 0.09)',
              border: '1px solid #DFDFDF',
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <span className='text-14 xl:text-16'>{option.label}</span>
          </MenuItem>
        ))}
      </Select>
    </StyledFormControl>
  );
};
